/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { YospaceAdsPlayer } from '../YospaceAdsPlayer';
import {
  Action,
  AdBreakInfo,
  AdContentTimePosition,
  AdInfo,
  AdTrackingEvent,
  BUFFERING_STATES,
  PLAYBACK_STATES,
  PlaybackStatistics,
  Player,
  PlayerContext,
  RequestType,
  SEEKING_STATES,
  SeekRangeInfo,
  StreamTimelineEvent,
  StreamTimelineMetadata,
  ThumbnailPreviewMetadata,
  TrackInfo,
  TrackType,
  VALUE_UNKNOWN,
  VerificationResource,
  AdVerification,
  AdEvent,
  PlaybackOptions,
  TrackVariantInfo,
  AdTechnology,
  AdEventListener,
} from '@quickplay/player-interface';
import { YospaceAdsSession } from '../YospaceAdsSession';
import {
  AnyFunc,
  createError,
  createEventEmitter,
  createLogger,
  Error,
  ErrorCodes,
  EventEmitter,
  eventObject,
  Logger,
  LoggerLevel,
} from '@quickplay/foundation';
import { TimedMetadataHandler } from './live/TimedMetadataHandler';
import {
  getSeekPositionFor,
  getVODSessionAdsDuration,
  getVODSessionDurationPriorTo,
} from './vod/policy/util/YospaceAdsPlaybackPolicyUtil';
import { createAdBreakInfo, createAdInfo } from './AdUtil';
import { toAdContentTimePosition } from '../util/YospaceAdsUtil';
import { YospaceSession } from './YospaceSession';
import { YospaceErrorCodes } from './YospaceErrorCodes';

const TAG_NAME = 'FL_YOSPACE_PLAYER';

export class YospacePlayer implements YospaceAdsPlayer {
  /**
   * Provides access to the internal player (fl-player).
   * @property {any} player
   */
  player: Player;
  /**
   * Provides access to the Yospace SDK.
   * @property {any} yospaceSDK
   */
  yospaceSDK: any;
  /**
   * Provides access to the Yospace session.
   */
  yospaceSession: YospaceAdsSession;
  /**
   * Provides access to this object's event emitter.
   * @property {EventEmitter} eventEmitter
   */
  private eventEmitter: EventEmitter = createEventEmitter();
  /**
   * Provides access to the internal player context.
   * @property {PlayerContext} internalPlayerContext
   */
  private internalPlayerContext: PlayerContext = PlayerContext.MAIN;
  /**
   * Managed timed metadata events from the player
   */
  private timedMetadataHandler?: TimedMetadataHandler;
  /**
   * Provides access to the logger.
   * @property {Logger} logger
   */
  private logger: Logger;
  /**
   * Provide access to the midroll ad break index.
   * @property {number} midrollAdBreakIndex
   */
  private midrollAdBreakIndex = 0;
  /**
   * Provides access to the Yospace Ads session.
   * @property {any} session
   */
  private session: any;

  private isPlaybackPaused = false;
  private isPlayerSeeking = false;
  private isPlayerBuffering = false;

  private activeAdBreakInfo?: AdBreakInfo = undefined;

  private activeAdInfo?: AdInfo = undefined;

  private shouldReportAdProgress = false;
  private currentAdStartTime?: number = undefined;
  private currentAdEndTime?: number = undefined;

  private isAdVerificationStarted = false;

  private vodSessionAdsDuration = 0;

  /**
   * Yospace player constructor.
   * @param player the fl-player instance
   * @param yospaceSession the Yospace session
   * @param yospaceSDK the Yospace SDK
   * @param logger the logger
   */
  constructor(player: any, yospaceSession: YospaceAdsSession, yospaceSDK: any, logger?: Logger) {
    this.logger = logger || createLogger(LoggerLevel.OFF, TAG_NAME);
    this.player = player;
    this.session = yospaceSession.session;
    this.yospaceSession = yospaceSession;
    this.yospaceSDK = yospaceSDK;

    if (this.session && this.session instanceof this.yospaceSDK.SessionVOD) {
      const yospaceAdsSession = yospaceSession as YospaceSession;

      yospaceAdsSession.attachPlayerToPolicyHandler(this);
    }

    this.initialize();
  }

  private initialize = (): void => {
    // custom initialization for each session type
    this.initializeBySessionType();

    // subscribe to Shaka player events
    this.subscribeToInternalPlayerEvents();

    if (!this.session) {
      this.logger.warn(
        'Session is invalid. Please ensure a valid YospaceAdsSession instance is passed via YospaceAdsPlayerFactory.createYospaceAdsPlayer',
      );
      return;
    }

    // attach the ad analytics observer
    this.session.addAnalyticObserver({
      onAdvertBreakStart: (adBreak: any): void => {
        this.logger.log('onAdvertBreakStart', adBreak);

        const trackingEvent = AdTrackingEvent.AD_BREAK_STARTED_EVENT;

        this.logger.log(`trackingEvent:`, trackingEvent);

        this.internalPlayerContext = PlayerContext.AD;

        this.eventEmitter.publish('playercontextchanged', this.internalPlayerContext);

        if (adBreak !== null) {
          const timePosition = toAdContentTimePosition(adBreak.getPosition());
          const adBreakIndex =
            this.session instanceof this.yospaceSDK.SessionVOD ? this.getAdBreakIndex(timePosition) : VALUE_UNKNOWN;

          this.activeAdBreakInfo = this.toAdBreakInfo(adBreak, timePosition, adBreakIndex, this.player);
        }

        this.logger.log(`ActiveAdBreakInfo`, this.activeAdBreakInfo);
        this.eventEmitter.publish(AdEvent.ADVERT_BREAK_START, this.activeAdBreakInfo);
      },

      /**
       * Called when an advertisement break ends
       */
      onAdvertBreakEnd: (): void => {
        this.logger.log(`onAdvertBreakEnd`);

        const trackingEvent = AdTrackingEvent.AD_BREAK_ENDED_EVENT;

        this.logger.log(`trackingEvent:`, trackingEvent);

        const yospaceSession = this.yospaceSession as YospaceSession;
        yospaceSession.evaluateSeek();

        this.internalPlayerContext = PlayerContext.MAIN;

        this.eventEmitter.publish('playercontextchanged', this.internalPlayerContext);
        this.eventEmitter.publish(AdEvent.ADVERT_BREAK_END, this.activeAdBreakInfo);

        this.activeAdBreakInfo = undefined;
      },

      /**
       * Called when playback of an advertisement starts
       */
      onAdvertStart: (advert: any): void => {
        this.logger.log(`onAdvertStart`, advert);

        const trackingEvent = AdTrackingEvent.AD_STARTED_EVENT;
        this.logger.log(`trackingEvent:`, trackingEvent);

        /*if (this.adPlaybackPolicy) {
          this.adPlaybackPolicy.setAdTrackingEvent(trackingEvent);
        }*/

        this.activeAdInfo = this.toAdInfo(advert, this.player, this.activeAdBreakInfo);

        this.shouldReportAdProgress = true;
        this.currentAdStartTime = advert.getStart();
        this.currentAdEndTime = advert.getStart() + advert.getDuration();

        this.logger.log('ActiveAdInfo', this.activeAdInfo);
        this.eventEmitter.publish(AdEvent.ADVERT_START, this.activeAdInfo);

        this.onAdVerificationStart(advert);
      },

      /**
       * Called when playback of an advertisement ends
       */
      onAdvertEnd: (): void => {
        this.logger.log(`onAdvertEnd`);
        const trackingEvent = AdTrackingEvent.AD_ENDED_EVENT;
        this.logger.log(`trackingEvent:`, trackingEvent);

        /*if (this.adPlaybackPolicy) {
          this.adPlaybackPolicy.setAdTrackingEvent(trackingEvent);
        }*/

        this.shouldReportAdProgress = false;
        this.currentAdStartTime = undefined;
        this.currentAdEndTime = undefined;

        this.eventEmitter.publish(AdEvent.ADVERT_END, this.activeAdInfo);

        this.onAdVerificationEnd();
      },

      /**
       * Indicates that a VMAP payload was received and processed from the Central Streaming Manager during playback
       * of a stream in respect of upcoming adverts. A client can read the adBreak update from the Session.
       */
      onAnalyticUpdate(): void {
        // no-op
      },

      /**
       * Called when a tracking URL is called against the specified Advert
       * @param type the tracking type
       */
      onTrackingEvent: (type: string): void => {
        const trackingEvent = this.toAdTrackingEvent(type);
        this.logger.log(`trackingEvent:`, trackingEvent);

        this.onAdTrackingEvent(trackingEvent);
      },

      /**
       * Called when an ad break returns early
       * @param adBreak the ad break that has returned
       */
      onAdvertBreakEarlyReturn: (adBreak: any): void => {
        this.logger.log('onAdvertBreakEarlyReturn', adBreak);
      },

      /**
       * Called when a session reports an error
       * @param errorCode the error code associated with the event
       */
      onSessionError: (errorCode: any): void => {
        if (errorCode === this.yospaceSDK.SessionErrorCode.TIMEOUT) {
          this.logger.log(
            'onSessionError: The session with the Yospace Central Streaming Manager (CSM) service timed-out. No further analytics will be signalled after this event.',
          );
          // Refer: [RSM-130]
          // When user switches to background and comes back after minutes(let say after 3 - 5 minutes), yospace session fails with 400 http errorcode and gets timed out.
          // Since this is a FATAL error and the player wasn't able to recover and continuosly retries for the chunk which makes player to buffer infinitely.
          // To handle this, We abort the player by throwing an appropriate error.
          this.player.abort(this.createSessionError(errorCode, 'The session with the Yospace CSM service timed-out'));
        } else if (errorCode === this.yospaceSDK.SessionErrorCode.UNRESOLVED_BREAK) {
          this.logger.log(
            'onSessionError: The fulfillment payload for an initial VMAP response with placeholder break was not received or did not contain the expected data.',
          );
        } else {
          this.logger.log('onSessionError: code', errorCode);
        }
      },

      /**
       * Called when a session reports a tracking error
       * @param trackingError the error object associated with the event
       */
      onTrackingError: (trackingError: any): void => {
        this.logger.log('onTrackingError', trackingError);
      },
    });

    if (this.session && this.session instanceof this.yospaceSDK.SessionVOD) {
      this.vodSessionAdsDuration = getVODSessionAdsDuration(this.session, this.yospaceSDK.BreakType.LINEAR);

      this.logger.log(`VOD session duration ${this.vodSessionAdsDuration}`);
    }
  };

  private initializeBySessionType = (): void => {
    if (this.session && this.session instanceof this.yospaceSDK.SessionLive) {
      this.timedMetadataHandler = new TimedMetadataHandler(this, this.session, this.yospaceSDK, this.logger);
    } else if (this.session && this.session instanceof this.yospaceSDK.SessionVOD) {
      this.vodSessionAdsDuration = getVODSessionAdsDuration(this.session, this.yospaceSDK.BreakType.LINEAR);

      if (this.player.playbackOptions && this.player.playbackOptions.initialPlaybackTime) {
        let initialPlaybackTimeMs = getSeekPositionFor(
          this.player.playbackOptions.initialPlaybackTime * 1000,
          this.session,
          this.yospaceSDK.BreakType.LINEAR,
        );

        initialPlaybackTimeMs = this.session.willSeekTo(initialPlaybackTimeMs);

        this.logger.log(`[AD POLICY] overriding initial playback time`, initialPlaybackTimeMs);

        // override the initial playback time
        this.player.setPlaybackOptions({
          ...this.player.playbackOptions,
          initialPlaybackTime: initialPlaybackTimeMs / 1000,
        });
      }
    }
  };

  private createSessionError = (errorCode: number, message: string): Error => {
    const internalError = errorCode ? createError(errorCode, message) : undefined;
    return createError(
      YospaceErrorCodes.PLAYBACK_SESSION_FAILURE |
        ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK |
        ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
      message,
      internalError,
    );
  };

  private playbackStateChanged_ = (playbackState: PLAYBACK_STATES): void => {
    this.logger.log(`onPlaybackStateChanged`, playbackState);

    switch (playbackState) {
      case PLAYBACK_STATES.IDLE: {
        // do nothing
        break;
      }
      case PLAYBACK_STATES.LOADING: {
        // do nothing
        break;
      }
      case PLAYBACK_STATES.LOADED: {
        const cuePoints = this.session
          .getAdBreaksByType(this.yospaceSDK.BreakType.LINEAR)
          .map((adBreak: any) => adBreak.getStart());
        if (cuePoints && cuePoints.length) {
          this.eventEmitter.publish('adcuepoints', cuePoints);
        }
        break;
      }
      case PLAYBACK_STATES.STARTED: {
        if (this.isPlaybackPaused) {
          this.logger.log(`onPlayerEvent -> PlayerEvent.RESUME with playhead position`);

          this.session.onPlayerEvent(this.yospaceSDK.PlayerEvent.RESUME);
          this.isPlaybackPaused = false;
        } else {
          this.logger.log(`onPlayerEvent -> PlayerEvent.START with playhead position`);

          this.session.onPlayerEvent(this.yospaceSDK.PlayerEvent.START);
        }

        break;
      }
      case PLAYBACK_STATES.PAUSED: {
        this.logger.log(`onPlayerEvent -> PlayerEvent.PAUSE with playhead position`);

        this.isPlaybackPaused = true;

        this.session.onPlayerEvent(this.yospaceSDK.PlayerEvent.PAUSE);

        break;
      }
      case PLAYBACK_STATES.STOPPED: {
        this.logger.log(`onPlayerEvent -> PlayerEvent.STOP with playhead position`);

        this.session.onPlayerEvent(this.yospaceSDK.PlayerEvent.STOP);

        this.releaseSession();

        break;
      }
      default: {
        // do nothing
      }
    }
  };

  /**
   * Listens for seeking state changes.
   * @param seekingState the new seeking state
   */
  private onSeekingStateChanged_ = (seekingState: SEEKING_STATES): void => {
    this.logger.log(`onSeekingStateChanged`, seekingState);

    switch (seekingState) {
      case SEEKING_STATES.ACTIVE: {
        this.isPlayerSeeking = true;
        break;
      }
      case SEEKING_STATES.INACTIVE: {
        if (this.isPlayerSeeking) {
          this.session.onPlayerEvent(this.yospaceSDK.PlayerEvent.SEEK, this.player.currentTime * 1000);
          this.isPlayerSeeking = false;
          if (this.timedMetadataHandler) {
            this.timedMetadataHandler.onSeek();
          }
        }
        break;
      }
    }
  };

  /**
   * Listens for buffering state changes.
   * @param bufferingState the new buffering state
   */
  private onBufferingStateChanged_ = (bufferingState: BUFFERING_STATES): void => {
    this.logger.log(`onBufferingStateChanged`, bufferingState);

    switch (bufferingState) {
      case BUFFERING_STATES.ACTIVE: {
        this.isPlayerBuffering = true;
        this.session.onPlayerEvent(this.yospaceSDK.PlayerEvent.STALL);
        break;
      }
      case BUFFERING_STATES.INACTIVE: {
        if (this.isPlayerBuffering) {
          this.session.onPlayerEvent(this.yospaceSDK.PlayerEvent.CONTINUE);
          this.isPlayerBuffering = false;
        }
        break;
      }
    }
  };

  /**
   * Listens for player progress update.
   */
  private onProgressUpdate_ = (): void => {
    if (
      this.activeAdInfo &&
      this.shouldReportAdProgress &&
      this.currentAdStartTime !== undefined &&
      this.currentAdEndTime !== undefined &&
      this.player.currentTime <= this.currentAdEndTime
    ) {
      const progress = this.player.currentTime * 1000 - this.currentAdStartTime;
      this.eventEmitter.publish('adprogressupdate', this.activeAdInfo, progress);
    }
    this.session.onPlayheadUpdate(this.player.currentTime * 1000);

    if (this.timedMetadataHandler) {
      this.timedMetadataHandler.onProgress(this.player.currentTime * 1000);
    }
  };

  /**
   * Listens for player abort.
   */
  private onAborted_ = (): void => {
    this.logger.log(`onAborted`);
  };

  /**
   * Listens when content is loaded.
   */
  private onContentLoaded_ = (): void => {
    const adBreaks: AdInfo[] = [];
    this.session.getAdBreaksByType(this.yospaceSDK.BreakType.LINEAR).forEach((adBreak: any) => {
      if (adBreak) {
        const timePosition = this.toAdContentTimePosition(adBreak.getPosition());
        const adBreakIndex =
          this.session instanceof this.yospaceSDK.SessionVOD ? this.getAdBreakIndex(timePosition) : VALUE_UNKNOWN;
        const adBreakInfo: AdBreakInfo = this.toAdBreakInfo(adBreak, timePosition, adBreakIndex, this.player);
        adBreak.getAdverts().map((adVert: any) => {
          const info: AdInfo = this.toAdInfo(adVert, this.player, adBreakInfo);
          adBreaks.push(info);
        });
      }
      this.eventEmitter.publish('adbreaks', adBreaks);
    });
  };

  /**
   * Listens when tracks change.
   */
  private onTracksChanged_ = (): void => {
    this.logger.log(`onTracksChanged`);
  };

  /**
   * Listens when playback rate change.
   */
  private onRateChanged_ = (): void => {
    this.logger.log('onRateChanged');
  };

  /**
   * Listens when player volume change.
   */
  private onVoumeChanged_ = (): void => {
    this.logger.log('onVolumeChanged');
  };

  /**
   * Listens for timed metadata.
   * @param event
   */
  private onTimedMetadata_ = (event: any): void => {
    if (this.timedMetadataHandler) {
      this.timedMetadataHandler.enqueue(event);
    }
  };

  /**
   * Listens for errors.
   * @param _error the error
   */
  private onError_ = (_error: any): void => {
    if (this.activeAdBreakInfo) {
      // if playback fails in the middle of an ad-break, emit 'aderror'
      this.eventEmitter.publish('aderror', _error);
    }
    this.releaseSession();
  };

  private subscribeToInternalPlayerEvents = (): void => {
    this.player.subscribe('playbackstatechanged', this.playbackStateChanged_);
    this.player.subscribe('seekingstatechanged', this.onSeekingStateChanged_);
    this.player.subscribe('bufferingstatechanged', this.onBufferingStateChanged_);
    this.player.subscribe('progressupdate', this.onProgressUpdate_);
    this.player.subscribe('aborted', this.onAborted_);
    this.player.subscribe('contentloaded', this.onContentLoaded_);
    this.player.subscribe('trackschanged', this.onTracksChanged_);
    this.player.subscribe('timedmetadata', this.onTimedMetadata_);
    this.player.subscribe('error', this.onError_);
    this.player.subscribe('ratechanged', this.onRateChanged_);
    this.player.subscribe('volumechanged', this.onVoumeChanged_);
  };

  private unsubscribeFromInternalPlayerEvents = (): void => {
    this.player.unsubscribe('playbackstatechanged', this.playbackStateChanged_);
    this.player.unsubscribe('seekingstatechanged', this.onSeekingStateChanged_);
    this.player.unsubscribe('bufferingstatechanged', this.onBufferingStateChanged_);
    this.player.unsubscribe('progressupdate', this.onProgressUpdate_);
    this.player.unsubscribe('aborted', this.onAborted_);
    this.player.unsubscribe('contentloaded', this.onContentLoaded_);
    this.player.unsubscribe('trackschanged', this.onTracksChanged_);
    this.player.unsubscribe('timedmetadata', this.onTimedMetadata_);
    // dev note: passing the reference to this.onError_ here causing an exception
    // which prevents other subscribed callbacks from firing
    this.player.unsubscribe('error');
    this.player.unsubscribe('ratechanged', this.onRateChanged_);
    this.player.unsubscribe('volumechanged', this.onVoumeChanged_);
  };

  private releaseSession = (): void => {
    this.midrollAdBreakIndex = 0;
    if (this.session) {
      this.session.shutdown();
      this.session = undefined;
    }

    // unsubscribe from all internal player events
    this.unsubscribeFromInternalPlayerEvents();
  };

  get playerContext(): PlayerContext {
    return this.internalPlayerContext;
  }

  get availableBitrates(): number[] {
    return this.player.availableBitrates;
  }

  get currentEpochTime(): number {
    return this.player.currentEpochTime;
  }

  get currentTime(): number {
    if (this.player.isLive) {
      return this.player.currentTime;
    }

    const adBreaksDurationMs = getVODSessionDurationPriorTo(
      this.player.currentTime * 1000,
      this.session,
      this.yospaceSDK.BreakType.LINEAR,
    );

    // Refer: [FLPS-229]
    // At random time the differnce between `player.currentTime` and `adBreakDurationMs` became exponential value `ex: 8.881784197001252e-16`
    // That causes the impact on main player `player.currentTime`
    // To resolve that behaviour we are taking the six digit decimal alone from `adbreakDurationMs` and `player.currentTime`

    const currentTime = Number(this.player.currentTime.toFixed(6)) - Number((adBreaksDurationMs / 1000).toFixed(6));

    return currentTime < 0 ? 0 : currentTime;
  }

  get duration(): number {
    if (
      this.player.isLive ||
      !this.session ||
      !this.session.getAdBreaksByType(this.yospaceSDK.BreakType.LINEAR).length
    ) {
      return this.player.duration;
    }

    const contentDurationMs = this.player.duration * 1000 - this.vodSessionAdsDuration;

    return contentDurationMs / 1000;
  }

  get internalPlayerName(): string {
    return this.player.internalPlayerName;
  }

  get internalPlayerVersion(): string {
    return this.player.internalPlayerVersion;
  }

  get isBuffering(): boolean {
    return this.player.isBuffering;
  }

  get isLive(): boolean {
    return this.player.isLive;
  }

  get isMute(): boolean | undefined {
    return this.player.isMute;
  }

  get playbackOptions(): PlaybackOptions | undefined {
    return this.player.playbackOptions;
  }

  get isSeeking(): boolean {
    return this.player.isSeeking;
  }

  get maxBitrate(): number | undefined {
    return this.player.maxBitrate;
  }

  set maxBitrate(maxBitrate) {
    this.player.maxBitrate = maxBitrate;
  }

  get minBitrate(): number | undefined {
    return this.player.minBitrate;
  }

  set minBitrate(minBitrate) {
    this.player.minBitrate = minBitrate;
  }

  get playbackRate(): number {
    return this.player.playbackRate;
  }
  set playbackRate(playbackRate) {
    this.player.playbackRate = playbackRate;
  }

  get playbackStatistics(): PlaybackStatistics | undefined {
    return this.player.playbackStatistics;
  }

  get playerState(): string {
    return this.player.playerState;
  }

  get rawLibrary(): any {
    return this.player.rawLibrary;
  }

  get rawPlayer(): any {
    return this.player.rawPlayer;
  }

  get remainingTime(): number {
    return 0;
  }

  get volume(): number {
    return this.player.volume;
  }

  set volume(volumeLevel: number) {
    this.player.volume = volumeLevel;
  }

  get adInsertionType(): AdTechnology {
    return AdTechnology.SSAI;
  }

  adEventListener?: AdEventListener;

  adSessionId?: string;

  abort(error: Error): void {
    this.player.abort(error);
  }

  send(eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void {
    this.player.send(eventName, action, metadata);
  }

  currentOffsetFromLiveEdge(): number {
    return this.player.currentOffsetFromLiveEdge();
  }

  discardAdBreak(): void {
    //TODO: implement
  }

  resize(_width: number, _height: number, _isFullscreen: boolean): void {
    // no-op
  }
  getActiveTracks(): TrackInfo[] {
    return this.player.getActiveTracks();
  }

  getVariantTracks(): TrackVariantInfo[] {
    return this.player.getVariantTracks();
  }

  getAvailableTrackVariants(trackType: TrackType): TrackVariantInfo[] {
    return this.player.getAvailableTrackVariants(trackType);
  }

  getAllTracks(): TrackInfo[] {
    return this.player.getAllTracks();
  }

  getCuePoints(): number[] {
    if (this.session.getPlaybackMode() === this.yospaceSDK.PlaybackMode.VOD) {
      return this.session.getAdBreaksByType(this.yospaceSDK.BreakType.LINEAR).map((adBreak: any) => adBreak.getStart());
    }

    return [];
  }

  load(): void {
    this.player.load();
  }

  muteOrUnmute(): void {
    this.player.muteOrUnmute && this.player.muteOrUnmute();
  }

  pause(): void {
    this.player.pause();
  }

  play(): void {
    this.player.play();
  }

  seek(seekPoint: number): void {
    if (
      this.player.isLive ||
      !this.session ||
      !this.session.getAdBreaksByType(this.yospaceSDK.BreakType.LINEAR).length
    ) {
      this.player.seek(seekPoint);
    } else {
      this.player.seek(
        this.session.willSeekTo(
          getSeekPositionFor(seekPoint >= 0 ? seekPoint * 1000 : 0, this.session, this.yospaceSDK.BreakType.LINEAR),
        ) / 1000,
      );
    }
  }

  seekToLiveEdge(): void {
    this.player.seekToLiveEdge();
  }

  seekableRange(): SeekRangeInfo {
    if (this.player.isLive) {
      return this.player.seekableRange();
    }

    return {
      start: 0,
      end: this.duration,
    };
  }

  selectTrack(track?: TrackInfo): void {
    this.player.selectTrack(track);
  }

  setRequestInterceptor(requestInterceptor: (requestType: RequestType, request: any) => void): void {
    this.player.setRequestInterceptor(requestInterceptor);
  }

  setResponseInterceptor(responseInterceptor: (requestType: RequestType, response: any) => void): void {
    this.player.setResponseInterceptor(responseInterceptor);
  }

  //returns metatData for the corresponding position
  async getThumbnail(position: number): Promise<ThumbnailPreviewMetadata | undefined> {
    return this.player.getThumbnail(position);
  }

  skipAd(): void {
    //TODO: implement
  }

  stop(): Promise<void> {
    return this.player.stop();
  }

  accessFunctionHandler(json: string): void {
    this.player.accessFunctionHandler(json);
  }

  subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.eventEmitter.subscribe(eventName, callback);
    this.player.subscribe(eventName, callback);
  }

  unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.eventEmitter.unsubscribe(eventName, callback);
    this.player.unsubscribe(eventName, callback);
  }

  private toAdContentTimePosition = (timePosition: string): AdContentTimePosition => {
    switch (timePosition) {
      case 'preroll':
        return AdContentTimePosition.PREROLL;
      case 'midroll':
        return AdContentTimePosition.MIDROLL;
      case 'postroll':
        return AdContentTimePosition.POSTROLL;
      default:
        return AdContentTimePosition.UNKNOWN;
    }
  };

  private getAdBreakIndex = (timePosition: AdContentTimePosition): number => {
    switch (timePosition) {
      case AdContentTimePosition.PREROLL:
        return 0;
      case AdContentTimePosition.MIDROLL:
        return ++this.midrollAdBreakIndex;
      case AdContentTimePosition.POSTROLL:
        return -1;
      default:
        return VALUE_UNKNOWN;
    }
  };

  private toAdBreakInfo = (
    adBreak: any,
    timePosition: AdContentTimePosition,
    adBreakIndex: number,
    player: any,
  ): AdBreakInfo => {
    return createAdBreakInfo(adBreak, timePosition, adBreakIndex, player);
  };

  private toAdInfo = (advert: any, player: Player, activeAdBreakInfo?: AdBreakInfo): AdInfo => {
    return createAdInfo(advert, player, activeAdBreakInfo);
  };

  private toAdTrackingEvent = (trackingEvent: string): AdTrackingEvent => {
    switch (trackingEvent) {
      case 'firstQuartile':
        return AdTrackingEvent.AD_FIRST_QUARTILE_EVENT;
      case 'midpoint':
        return AdTrackingEvent.AD_MIDPOINT_EVENT;
      case 'thirdQuartile':
        return AdTrackingEvent.AD_THIRD_QUARTILE_EVENT;
      case 'pause':
        return AdTrackingEvent.AD_PAUSED_EVENT;
      case 'resume':
        return AdTrackingEvent.AD_RESUMED_EVENT;
      case 'mute':
        return AdTrackingEvent.AD_MUTED_EVENT;
      case 'unmute':
        return AdTrackingEvent.AD_UNMUTED_EVENT;
      case 'skip':
        return AdTrackingEvent.AD_SKIPPED_EVENT;
      case 'playerExpand':
        return AdTrackingEvent.AD_PLAYER_EXPANDED_EVENT;
      case 'playerCollapse':
        return AdTrackingEvent.AD_PLAYER_COLLAPSED_EVENT;
      case 'adExpand':
        return AdTrackingEvent.AD_EXPANDED_EVENT;
      case 'adCollapse':
        return AdTrackingEvent.AD_COLLAPSED_EVENT;
      default:
        return AdTrackingEvent.AD_UNKNOWN_EVENT;
    }
  };

  /**
   * Starts the current advert verification and emits a signal to let the client know.
   * @param advert the advert to verify
   */
  private onAdVerificationStart = (advert: any): any => {
    this.logger.log(`Open Measurement Ad verification: start`);

    // if the Advert isn't active, don't trigger Open Measurement Ad verification
    if (!advert.isActive()) {
      this.logger.log(`Open Measurement Ad verification: advert is not active`);
      return;
    }

    // if the Advert is filler, don't trigger Open Measurement Ad verification
    // note: in practice, filler ads won't have AdVerifications anyway
    if (advert.isFiller()) {
      this.logger.log(`Open Measurement Ad verification: advert is a filler`);
      return;
    }

    const flAdVerifications: AdVerification[] = [];

    // get the AdVerifications. there may be more than one.
    const yoAdVerifications = advert.getAdVerifications();

    yoAdVerifications.forEach((yoAdVerification: any) => {
      const vendorKey = yoAdVerification.getVendor();
      const params = yoAdVerification.getParameters();
      const resources = yoAdVerification.getResources();

      const verificationResources: VerificationResource[] = [];

      resources.forEach((resource: any) => {
        // Create a VerificationResource object
        const verificationResource = {
          name: resource.getName(),
          value: resource.getValue(),
          attributes: resource.getAttributes(),
        };
        verificationResources.push(verificationResource);
      });

      // create and AdVerification object
      const flAdVerification = {
        verificationResources: verificationResources,
        verificationParameters: params,
        vendorKey: vendorKey,
      };

      flAdVerifications.push(flAdVerification);
    });

    if (flAdVerifications.length) {
      this.isAdVerificationStarted = true;

      this.eventEmitter.publish(
        AdEvent.ADVERT_VERIFICATION_START,
        advert,
        flAdVerifications,
        //flVASTProperties,
        //flAdVideoProperties,
      );
    }
  };

  /**
   * Ends the current advert verification and emits a signal to let the client know.
   */
  private onAdVerificationEnd(): any {
    this.logger.log(`Open Measurement Ad verification: end`);

    if (this.isAdVerificationStarted) {
      this.isAdVerificationStarted = false;

      this.eventEmitter.publish(AdEvent.ADVERT_VERIFICATION_END);
    }
  }

  /**
   * Emits a signal on each advert tracking event.
   * @param adTrackingEvent the advert tracking event
   * @private
   */
  private onAdTrackingEvent(adTrackingEvent: AdTrackingEvent): any {
    this.logger.log(`Open Measurement Ad verification: ad event "${adTrackingEvent}"`);

    this.eventEmitter.publish(AdEvent.ADVERT_TRACKING_EVENT, adTrackingEvent);
  }

  setPlaybackOptions(playbackOptions: PlaybackOptions): void {
    this.player.setPlaybackOptions(playbackOptions);
  }

  restrictQuality(enableRestriction: boolean): void {
    this.player.restrictQuality(enableRestriction);
  }

  unloadAndSavePreload(): Promise<void> {
    // no-op
    return Promise.resolve();
  }

  availableMimeTypes(trackType: TrackType): Array<string> {
    return this.player.availableMimeTypes(trackType);
  }

  setPreferredMimeTypes(trackType: TrackType, mimeTypes: Array<string>): void {
    return this.player.setPreferredMimeTypes(trackType, mimeTypes);
  }

  restoreFromPreload(): void {
    // no-op
  }
}
